/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import {
    getEstimatedAvailabilityAsync,
    searchByCriteriaAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { ReleasedProductType } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    // ArrayExtensions,
    buildCacheKey,
    // createInventoryAvailabilitySearchCriteria,
    IProductInventoryInformation,
    // mapAggregatedProductInventoryInformation,
    mapProductInventoryInformation
} from '@msdyn365-commerce-modules/retail-actions';

import { ActiveWishlistInput, getActiveWishlistItems } from './get-items-in-wishlists';

/**
 * Input class for availabilities for items in wishlist.
 */
export class ProductAvailabilitiesForWishlistItems implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('ActiveWishlistItemsAvailability', this.apiSettings);

    public getCacheObjectType = () => 'ActiveWishlistItemsAvailability';

    public dataCacheType = (): CacheType => 'request';
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>) => {
    return new ProductAvailabilitiesForWishlistItems(inputData.requestContext.apiSettings);
};

/**
 * Calls the Retail API to get wishlist which will be cached the then finds the quantities for each item.
 * @param input
 * @param ctx
 */
export async function getAvailabilitiesForWishlistItems(
    input: ProductAvailabilitiesForWishlistItems,
    ctx: IActionContext
): Promise<IProductInventoryInformation[]> {
    // If no input is provided fail out
    if (!input) {
        throw new Error('[getAvailabilitiesForWishlistItems]No valid Input was provided, failing');
    }

    const products = await getActiveWishlistItems(new ActiveWishlistInput(), ctx);

    if (!products) {
        ctx.trace('[getAvailabilitiesForWishlistItems] Not able to get products in wishlist');
        return <IProductInventoryInformation[]>[];
    }

    const validProducts: number[] = [];

    for (const product of products) {
        if (
            product.ProductDetails &&
            product.ProductDetails.RecordId &&
            product.ProductDetails.ItemTypeValue !== ReleasedProductType.Service
        ) {
            validProducts.push(product.ProductDetails.RecordId);
        }
    }

    if (validProducts.length === 0) {
        ctx.trace('[getAvailabilitiesForWishlistItems] No products in wishlist');
        return <IProductInventoryInformation[]>[];
    }
    const productAvailabilites = await getEstimatedAvailabilityAsync(
        { callerContext: ctx },
        { ProductIds: validProducts, DefaultWarehouseOnly: true }
    );

    /* VSI Customization - START - 04/01/21 */
    // Make product available by default if it is vendor shipped
    const vendorShippedProducts: number[] = [];
    const callerContext = ctx;
    const {
        requestContext: {
            apiSettings: { channelId, catalogId }
        }
    } = ctx;
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = { ChannelId: channelId, CatalogId: catalogId };
    searchCriteriaInput.Ids = validProducts;
    searchCriteriaInput.IncludeAttributes = true;
    // store product IDs in vendorShippedProducts array if product is vendor shipped
    const productsData = await searchByCriteriaAsync({ callerContext, queryResultSettings: {} }, searchCriteriaInput);
    productsData.map(product => {
        const attributes = product.AttributeValues;
        const isvendorShippedAttribute =
            attributes &&
            attributes.find(attribute => {
                const attributeName = attribute.Name && attribute.Name.trim().toLowerCase();
                return attributeName === 'isvendershipproduct';
            });
        const isVendorShiped =
            isvendorShippedAttribute &&
            isvendorShippedAttribute.TextValue &&
            isvendorShippedAttribute.TextValue.trim().toLowerCase() === 'yes';
        if (isVendorShiped) {
            vendorShippedProducts.push(product.RecordId);
        }
    });
    /* VSI Customization - END */

    if (productAvailabilites && productAvailabilites.ProductWarehouseInventoryAvailabilities) {
        const inventoryInformation = mapProductInventoryInformation(ctx, productAvailabilites.ProductWarehouseInventoryAvailabilities);
        /* VSI Customization - START - 21/12/20 */
        inventoryInformation &&
            inventoryInformation.map(inventoryInfo => {
                const isVendorProduct = vendorShippedProducts.find(recordId => {
                    return recordId === inventoryInfo.ProductAvailableQuantity.ProductId;
                });
                // Update quanity of product to 10 and set availability true if the product is shipped by vendor
                if (isVendorProduct) {
                    inventoryInfo.IsProductAvailable = true;
                    inventoryInfo.ProductAvailableQuantity.AvailableQuantity = 10;
                }
            });
        /* VSI Customization - END */
        return inventoryInformation;
    } else {
        ctx.trace('[getAvailabilitiesForWishlistItems] unable to get availabilites for product');
        return <IProductInventoryInformation[]>[];
    }
}
export const getAvailabilitiesForWishlistItemsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/wishlist/dobbies-wishlist-items/get-product-availabilities-wishlist-items',
    action: <IAction<IProductInventoryInformation[]>>getAvailabilitiesForWishlistItems,
    input: createInput
});

export default getAvailabilitiesForWishlistItemsDataAction;
